import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, CircleDashed, Save, Loader2, Volume2, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { speak } from "@/lib/audio";

const FIELDS = [
  { k: "ELEVENLABS_API_KEY", label: "ElevenLabs · Voce italiana", hint: "elevenlabs.io → Profile → API Keys", url: "https://elevenlabs.io/app/settings/api-keys" },
  { k: "PAYMENT_LINK", label: "Link pagamento esterno (Stripe/Wise)", hint: "Mostrato nella pagina Abbonamento", plain: true },
];

const KeyField = ({ f: { k, label, hint, url, plain }, st, value, onChange, onRemove }) => (
  <div>
    <div className="flex items-center gap-2 mb-1.5">
      {st?.configured ? <CheckCircle2 size={15} className="text-emerald-400" /> : <CircleDashed size={15} className="text-slate-500" />}
      <span className="text-sm text-white">{label}</span>
      {url && <a href={url} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-amber-300"><ExternalLink size={12} /></a>}
      <span className="ml-auto text-[11px] font-mono text-slate-500" data-testid={`key-status-${k}`}>{st?.configured ? st.preview : "non configurata"}</span>
    </div>
    <input type={plain ? "url" : "password"} autoComplete="off" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      placeholder={st?.configured ? "Lascia vuoto per mantenere · incolla per sostituire" : hint} data-testid={`key-input-${k}`}
      className="w-full bg-[#050b18]/80 border border-cyan-500/25 rounded-xl px-4 py-2.5 text-sm font-mono outline-none focus:border-cyan-400/60" />
    {st?.configured && <button onClick={onRemove} className="text-[11px] text-rose-300/80 hover:text-rose-300 mt-1" data-testid={`key-remove-${k}`}>Rimuovi</button>}
  </div>
);

const VoicePicker = ({ state, voices, onLoad, onPick }) => (
  <div className="hud-card p-5">
    <p className="hud-tag mb-2">Voce italiana ElevenLabs</p>
    <p className="text-sm text-slate-400 mb-3">Scegli la voce madrelingua di Aurora (modello eleven_flash_v2_5). Attuale: <span className="font-mono text-amber-300" data-testid="current-voice-id">{state.ELEVENLABS_VOICE_ID?.preview || "predefinita"}</span></p>
    <button onClick={onLoad} disabled={!state.ELEVENLABS_API_KEY?.configured} data-testid="load-voices-btn" className="btn-ghost-hud rounded-full px-4 py-2 text-xs disabled:opacity-40">Carica voci dal mio account</button>
    <div className="mt-4 space-y-1.5 max-h-[420px] overflow-y-auto">
      {voices.map((v) => (
        <button key={v.voice_id} onClick={() => onPick(v.voice_id)} data-testid={`voice-pick-${v.voice_id}`}
          className="w-full flex items-center gap-2 rounded-lg border border-cyan-500/15 px-3 py-2 text-left hover:border-amber-300/60 transition-colors">
          <Volume2 size={14} className="text-cyan-300" />
          <span className="text-sm text-white flex-1 truncate">{v.name}</span>
          {v.italian && <span className="text-[10px] font-mono uppercase text-emerald-300 border border-emerald-400/40 rounded-full px-2">IT</span>}
        </button>
      ))}
    </div>
    <p className="text-[11px] text-slate-500 mt-3">Suggerimento: aggiungi una voce italiana dalla Voice Library di ElevenLabs al tuo account, poi ricarica l'elenco.</p>
  </div>
);

export const AdminKeys = () => {
  const [state, setState] = useState(null);
  const [vals, setVals] = useState({});
  const [voices, setVoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/integrations").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/integrations", { values: { ...vals, ...extra } });
      setState(data);
      setVals({});
      toast.success("Chiavi salvate e cifrate");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const loadVoices = async () => {
    try { const { data } = await api.get("/admin/elevenlabs/voices"); setVoices(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const pickVoice = async (id) => {
    await save({ ELEVENLABS_VOICE_ID: id });
    speak("Ciao, sono Aurora. Questa è la mia nuova voce italiana.", "italiana").catch(() => toast.message("Voce salvata. Tocca di nuovo per ascoltarla."));
  };

  if (!state) return <p className="hud-tag">Caricamento…</p>;
  return (
    <div className="grid lg:grid-cols-[1.3fr_1fr] gap-5" data-testid="admin-keys">
      <div className="hud-card p-5 space-y-4">
        <p className="hud-tag flex items-center gap-2"><KeyRound size={13} /> Chiavi API PRO · salvate cifrate AES-256</p>
        {FIELDS.map((f) => <KeyField key={f.k} f={f} st={state[f.k]} value={vals[f.k]} onChange={(v) => setVals({ ...vals, [f.k]: v })} onRemove={() => save({ [f.k]: "" })} />)}
        <button onClick={() => save()} disabled={busy || !Object.values(vals).some((v) => v?.trim())} data-testid="keys-save-btn"
          className="btn-aurora rounded-xl px-6 py-2.5 text-sm uppercase flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salva chiavi
        </button>
        <p className="text-xs text-slate-500">Le chiavi hanno effetto immediato (senza riavvio) e non vengono mai mostrate per intero.</p>
      </div>
      <VoicePicker state={state} voices={voices} onLoad={loadVoices} onPick={pickVoice} />
    </div>
  );
};
